"use client";

import React from "react";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent, richTextComponent } from "@/sanity/sanityPortableText";
import Header from "@/components/global/partials/Header";
import Footer from "@/components/global/partials/Footer";
import { PageLegalSchema } from "@/sanity/pages/PageLegalTemplate";

import { AnimatePresence, motion } from "framer-motion";
import Noise from "@/shadcn/Noise";

import decoLines from "@/public/decos/lines-deco-big.svg";
import Image from "next/image";

export default function PageLegalJSX({ data }: { data: PageLegalSchema }) {
  const { title, blocksText } = data;

  return (
    <>
      <section className="relative w-full lg:px-0 px-[2rem] overflow-clip pb-[10rem]">
        {data?.settings?.header && (
          <Header
            data={data.settings.header}
            headerStyle="light"
            className="lg:top-[1rem]"
            showBorder
          />
        )}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 lg:w-full lg:h-full w-[266.4515rem] h-[146.59263rem]">
          <motion.div
            className="relative w-full h-full"
            initial={{
              background: "radial-gradient(176.45% 100% at 50% 0%, #050200 50%, #F50 80%, #FFF 100%)",
            }}
            animate={{
              background: "radial-gradient(176.45% 100% at 50% 0%, #050200 18.47%, #F50 64.16%, #FFF 100%)",
            }}
            transition={{
              duration: 1.8,
              ease: [0.22, 1, 0.36, 1],
              delay: 0,
            }}>
            <div className="hidden w-full h-full lg:block">
              <Noise
                patternSize={180}
                patternScaleX={2}
                patternScaleY={2}
                patternRefreshInterval={10}
                patternAlpha={10}
                gradientFade
              />
            </div>
          </motion.div>
        </div>

        <Image
          src={decoLines}
          alt=""
          className="hidden absolute left-0 lg:block w-[28.375rem]"
        />

        <Image
          src={decoLines}
          alt=""
          className="hidden absolute right-0 lg:block w-[28.375rem]"
        />

        <hr className="hidden absolute top-0 h-full border-r lg:block left-[28.375rem] z-[5] border-r-white/30" />
        <hr className="hidden absolute top-0 h-full border-l lg:block right-[28.375rem] z-[5] border-l-white/30" />

        <div className="relative z-[5] w-[min(96.125rem,100%)] mx-auto pt-[18.25rem]">
          {title && (
            <h1 className="font-[400] leading-[100%] lg:text-[8rem] bg-gradient-to-b from-[#FFFFFF] to-[#CFCFCF] bg-clip-text text-transparent">
              {title}
            </h1>
          )}

          {blocksText?.length > 0 && (
            <div className="mx-auto">
              <div className="flex bg-black py-[0.25rem] px-[1rem] w-fit gap-[6rem]">
                <p className="m-0 text-white leading-[140%] font-spacemono uppercase font-[400] text-[1.75rem]">
                  last updated
                </p>

                <p className="m-0 text-white leading-[140%] font-spacemono uppercase font-[400] text-[1.75rem]">
                  Apr 20, 2026
                </p>
              </div>
              <div className="flex flex-col bg-white gap-[2.5rem] p-[1rem]">
                {blocksText.map((block, index) => (
                  <React.Fragment key={index}>
                    <div className="p-[3rem]">
                      <PortableText
                        value={block.text ?? {}}
                        components={customRichTextComponent({
                          block: {
                            h2: ({ children }) => {
                              return (
                                <h2 className="lg:text-[4rem] leading-[120%] lg:leading-[150%] pr-[2rem] lg:pr-0 mb-[3rem]">
                                  {index + 1}. {children}
                                </h2>
                              );
                            },

                            h3: ({ children }) => {
                              return (
                                <h3 className="lg:text-[4rem] leading-[120%] lg:leading-[150%] pr-[2rem] lg:pr-0 ">
                                  {index + 1}. {children}
                                </h3>
                              );
                            },
                            normal: ({ children }) => {
                              return <p className="text-black leading-[150%] m-0 text-[2.25rem]">{children}</p>;
                            },
                          },
                        })}
                      />
                    </div>

                    {index < blocksText.length - 1 && (
                      <div className="px-[3rem]">
                        <hr className="w-full border-t h-[.125rem] border-t-black/20" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
